import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaPinterestP,
  FaYoutube,
  FaInstagram,
} from 'react-icons/fa6';
import { FiMail, FiPhone } from 'react-icons/fi';
import { apiGetSitePage } from '../../api';
import { usePageVisibility } from '../../context/PageVisibilityContext';
import footerBackground from '../../assets/FotterBG.png';

const pageKeyByPath = {
  '/about': 'about', '/contact': 'contact', '/career': 'career',
  '/featured-video': 'featured', '/payment': 'payment',
  '/secure-shopping': 'secure', '/shipping': 'shipping', '/return': 'return',
  '/faq': 'faq', '/affiliate': 'affiliate', '/privacy': 'privacy',
  '/terms': 'terms', '/copyright': 'copyright', '/sitemap': 'sitemap',
};

const socialIcons = {
  Facebook: { Icon: FaFacebookF, color: 'text-[#1877F2]' },
  Twitter: { Icon: FaTwitter, color: 'text-black' },
  LinkedIn: { Icon: FaLinkedinIn, color: 'text-[#0A66C2]' },
  Pinterest: { Icon: FaPinterestP, color: 'text-[#E60023]' },
  YouTube: { Icon: FaYoutube, color: 'text-[#FF0000]' },
  Instagram: { Icon: FaInstagram, color: 'text-[#E1306C]' },
};

const FooterColumn = ({ title, items }) => (
  <section>
    <h4 className="mb-4 text-base font-semibold text-orange-500">{title}</h4>
    <ul className="space-y-3 list-none">
      {items.map((item) => (
        <li key={item.label}>
          <Link to={item.to} className="text-sm text-white hover:text-orange-400 transition">
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

const Footer = () => {
  const { isPageActive } = usePageVisibility();
  const [data, setData] = useState({
    company_links: [],
    policy_links: [],
    help_links: [],
    misc_links: [],
    social_links: [],
    features: [],
    contact: [],
    app_links: [],
  });

  useEffect(() => {
    let active = true;
    apiGetSitePage('footer')
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        setData({
          company_links: d.company_links || [],
          policy_links: d.policy_links || [],
          help_links: d.help_links || [],
          misc_links: d.misc_links || [],
          social_links: d.social_links || [],
          features: d.features || [],
          contact: d.contact || [],
          app_links: d.app_links || [],
        });
      })
      .catch(() => {
        // Silent fail keeps footer empty rather than crashing the layout
      });
    return () => {
      active = false;
    };
  }, []);

  const contactByKey = (key) => (data.contact.find((c) => c.key === key) || {}).text || '';
  const appLinkByKey = (key) => (data.app_links.find((c) => c.key === key) || {}).url || '#';
  const visibleLinks = (items) => items.filter(({ to }) => {
    const pageKey = pageKeyByPath[to];
    return !pageKey || isPageActive(pageKey);
  });

  const email = contactByKey('email');
  const hours = contactByKey('hours');
  const phones = data.contact.filter((item) => {
    const normalizedKey = String(item.key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return normalizedKey.includes('phone') && String(item.text || '').trim();
  });

  return (
    <footer
      className="bg-cover bg-center bg-no-repeat text-white"
      style={{ backgroundImage: `url(${footerBackground})` }}
    >
      <div className="w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Main Grid */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8">
          <FooterColumn title="Company" items={visibleLinks(data.company_links)} />
          <FooterColumn title="Policies" items={visibleLinks(data.policy_links)} />
          <FooterColumn title="Help" items={visibleLinks(data.help_links)} />
          <FooterColumn title="Misc" items={visibleLinks(data.misc_links)} />

          {/* Connect with us */}
          <section className="col-span-2 w-full sm:col-span-1">
            <h4 className="mb-4 text-base font-semibold text-orange-500">Connect with us</h4>
            <address className="not-italic space-y-3 text-sm text-white">
              {email && (
                <div className="flex items-start gap-2">
                  <FiMail
                    className="mt-0.5 shrink-0 text-lg text-orange-500"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Email:</span>
                  <a href={`mailto:${email}`} className="break-all hover:text-orange-400">
                    {email}
                  </a>
                </div>
              )}
              {phones.map((phone, index) => (
                <div key={phone.id || phone.key || phone.text}>
                  <div className="flex items-center gap-2">
                    <FiPhone
                      className="shrink-0 text-lg text-orange-500"
                      aria-hidden="true"
                    />
                    <span className="sr-only">
                      {index === 0 ? 'Phone:' : `Additional phone ${index + 1}:`}
                    </span>
                    <a
                      href={`tel:${String(phone.text).replace(/[^+\d]/g, '')}`}
                      className="hover:text-orange-400"
                    >
                      {phone.text}
                    </a>
                  </div>
                  {index === phones.length - 1 && hours && (
                    <p className="ml-7 mt-1 text-xs text-white">{hours}</p>
                  )}
                </div>
              ))}
            </address>
          </section>

          {/* Download App */}
          <section className="col-span-2 w-full sm:col-span-1">
            <h4 className="mb-4 text-base font-semibold text-orange-500">Download Our App</h4>
            <ul className="w-full space-y-3 list-none">
              <li>
                <a
                  href={appLinkByKey('google_play')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-auto w-full max-w-[186px]"
                  />
                </a>
              </li>
              <li>
                <a
                  href={appLinkByKey('app_store')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                    alt="Download on the App Store"
                    className="h-auto w-full max-w-[186px]"
                  />
                </a>
              </li>
            </ul>
          </section>
        </section>

        {/* Features Strip */}
        <section className="border-t border-gray-700/60 mt-8 sm:mt-10 pt-5 sm:pt-6">
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 list-none">
            {data.features.map((f) => (
              <li
                key={f.id || f.key}
                className="flex items-center justify-center gap-2 text-xs sm:text-sm text-white text-center"
              >
                <span className="text-base sm:text-lg">{f.emoji}</span>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Bottom Bar */}
        <section className="border-t border-gray-700/60 mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <ul className="flex flex-wrap items-center justify-center gap-3 list-none">
            {data.social_links.map((s) => {
              const entry = socialIcons[s.name];
              if (!entry) return null;
              const { Icon, color } = entry;
              return (
                <li key={s.name}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    title={s.name}
                    className={`w-10 h-10 flex items-center justify-center bg-white rounded-full ${color} hover:scale-110 transition-transform shadow`}
                  >
                    <Icon className="text-base" />
                  </a>
                </li>
              );
            })}
          </ul>
          <p className="w-full pr-20 text-center text-xs leading-5 text-white sm:text-sm md:w-auto md:pr-0 md:text-left">
            Copyright &copy; 2018 by Ekamithila India
          </p>
        </section>
      </div>
    </footer>
  );
};

export default Footer;
